import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { AssessmentResult, AssessmentSettings, ExperienceType } from '@shared/models';
import { EChartsOption } from 'echarts';
import { AssessmentService } from '@shared/services/assessment.service';
import { getAssessmentDisplayParams, setupAssessmentChart } from '@shared/utils/assessment.utils';
import { UIState } from '@shared/directives/ui-state/ui-state.directive';
import { LinkService } from '@shared/services/link.service';
import { environment } from '@env/environment';
import { ReportActionId, ReportContext, ReportView, getReportContext, getReportView, isRegularPlayer } from './assessment-report.actions';

@Component({
  selector: 'assessment-report',
  templateUrl: './assessment-report.component.html',
  styleUrls: ['./assessment-report.component.scss']
})
export class AssessmentReportComponent implements OnInit, OnChanges {
  @Input() fromEditorContext: boolean = false;
  @Input() dialogMode: boolean;
  @Input() isLti: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() ltiReturnEnabled: boolean = false;
  @Input() canEdit: boolean;
  @Input() showBackToSimulation: boolean;
  @Input() isLastSimulation: boolean;
  @Input() hasClickedAssessmentButton: boolean;
  @Input() courseId: string;
  @Input() experienceType: ExperienceType;
  @Input() assessmentSettings?: AssessmentSettings;
  @Input() assessmentResult?: AssessmentResult;
  @Input() errorMessage?: string;
  @Input() externalLink?: any;
  @Input() posterUrl?: string;
  @Output() onShowSessionLogs = new EventEmitter();
  @Output() onRestart = new EventEmitter();
  @Output() onClose = new EventEmitter();
  @Output() onOpenAssessmentDashboard = new EventEmitter<boolean>();
  @Output() onOpenProgramIndex = new EventEmitter();
  @Output() onReturnToLms = new EventEmitter();

  public downloadInProgress: boolean = false;
  public displayParams: any;
  public chartOption: EChartsOption;
  public state: UIState;
  public view: ReportView;
  public context: ReportContext;

  private readonly _actionHandlers: Record<ReportActionId, () => void> = {
    sessionLogs: () => this.onShowSessionLogs.emit(),
    viewAllReports: () => this.onOpenAssessmentDashboard.emit(false),
    editAssessment: () => this.onOpenAssessmentDashboard.emit(true),
    close: () => this.onClose.emit(),
    restart: () => this.onRestart.emit(),
    download: () => this.downloadReport(),
    nextSimulation: () => this.continueOrGoToProgramIndex(),
    externalLink: () => this.applyExternalLink(),
    returnToLms: () => this.onReturnToLms.emit()
  };

  // Learner-facing report at the end of a session (indicative score, learner styles)
  get learnerReportMode(): boolean {
    return isRegularPlayer({ context: this.context, isLti: this.isLti });
  }

  // Same fallback as the program launcher when the experience has no poster
  get backgroundUrl(): string {
    return this.posterUrl || '/assets/wonda/background2_ld.jpg';
  }

  get hasExternalLink(): boolean {
    return this.externalLink && this.externalLink.enabled && this.externalLink.type && this.externalLink.target;
  }

  constructor(
    private _assessmentService: AssessmentService,
    private _linkService: LinkService
  ) {}

  ngOnInit(): void {
    this.setupWithAssessmentResult();
  }
  
  ngOnChanges() {
    this.setupWithAssessmentResult();
  }

  private setupWithAssessmentResult() {
    if (this.assessmentResult) {
      this.state = UIState.default;
      this.displayParams = getAssessmentDisplayParams(this.assessmentResult, this.assessmentSettings);
      this.chartOption = setupAssessmentChart(this.assessmentResult, this.displayParams.scoreMax, false);
    } else if (this.errorMessage) {
      this.state = UIState.error;
    } else {
      this.state = UIState.inprogress;
    }
    this.setupView();
  }

  private setupView() {
    this.context = getReportContext(this);
    this.view = getReportView({
      context: this.context,
      isLti: this.isLti,
      readOnly: this.readOnly,
      isSimulation: this.experienceType === ExperienceType.ai,
      hasResult: !!this.assessmentResult,
      canEdit: this.canEdit,
      hasClickedAssessmentButton: this.hasClickedAssessmentButton,
      hasExternalLink: !!this.hasExternalLink,
      ltiReturnEnabled: this.ltiReturnEnabled,
      showBackToSimulation: this.showBackToSimulation,
      isLastSimulation: this.isLastSimulation
    });
  }

  onAction(id: ReportActionId) {
    this._actionHandlers[id]();
  }

  downloadReport() {
    this.downloadInProgress = true;
    this._assessmentService
      .getAssessmentExportDataBySessionId(this.courseId, this.assessmentResult.session_id)
      .subscribe(success => {
        this.downloadInProgress = false;
      });
  }

  continueOrGoToProgramIndex() {
    if (this.showBackToSimulation || this.isLastSimulation) {
      this.onOpenProgramIndex.emit();
    } else {
      this.continueToNextSimulation();
    }
  }

  continueToNextSimulation() {
    this.openURL(`${environment.coursesApiUrl}/api/courses/${this.courseId}/simulation/next`);
  }
  private openURL(url: string) {
    (window.top || window).location.assign(url);
  }

  openHelp() {
    window.open('https://help.spaces.wondavr.com/en/articles/9728097-ai-assessment');
  }

  applyExternalLink() {
    if (!this.hasExternalLink) {
      return;
    }

    let target = this.externalLink.target;
    let newTab = this.externalLink.newTab;

    switch (this.externalLink?.type) {
      case "url":
        window.open(target, newTab ? "_blank" : "_top");
        break;
      case "experience":
        this._linkService.launchCourseById(target, newTab, this.experienceType);
        break;
    }
  }
}
